import { LanguagesEnum } from '@/api/enums';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppButton } from '@/ui/buttons';
import { RichTextRenderer } from '@/ui/rich-text-renderer';
import { FlexContainer } from '@/ui/styled-ui';
import { stripHtml } from '@/utils/helpers/courses.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { Dialog } from 'primereact/dialog';
import React from 'react';
import styled from 'styled-components';

const StyledFeedbackDialog = styled(Dialog)<{
  $isCorrect: boolean;
  $isMobile: boolean;
  $rtl?: boolean;
}>`
  direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
  width: 800px;
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);

  .p-dialog-content {
    display: flex;
    flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
    justify-content: space-between;
    color: var(--white-main);
    padding: var(--default-padding);
    background-color: ${({ $isCorrect }) =>
      $isCorrect ? 'var(--green-main)' : 'var(--red-main)'};
  }

  .p-dialog-header {
    display: none;
  }

  i {
    padding-right: var(--small-padding);
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
  }

  h5 {
    margin: 0px;
    line-height: var(--medium-line-height);
    font-size: var(--medium-font-size);
  }

  p {
    margin-bottom: 0px;
    white-space: pre-wrap;
  }

  .p-button {
    align-self: flex-end;
  }
`;

type QuizAnswerFeedbackProps = {
  isCorrect: boolean;
  explanation?: string;
  selectedAnswer?: string;
  attemptLanguage: LanguagesEnum;
  rtl?: boolean;
  onHide: () => void;
};

export const QuizAnswerFeedback: React.FC<QuizAnswerFeedbackProps> = ({
  isCorrect,
  explanation,
  selectedAnswer,
  attemptLanguage,
  rtl,
  onHide,
}) => {
  return (
    <StyledFeedbackDialog
      visible
      onHide={onHide}
      $isCorrect={isCorrect}
      appendTo={() => document.querySelector('.quiz-container') as HTMLElement}
      maskStyle={{
        position: 'absolute',
        alignItems: 'end',
        paddingBottom: '40px',
      }}
      maskClassName="quiz-answer-feedback-mask"
      $isMobile={isMobile()}
      $rtl={rtl}
    >
      <div className="mr-5">
        <FlexContainer align="center" justify="flex-start">
          <i className={isCorrect ? 'pi pi-check' : 'pi pi-times'} />
          <h5>
            {isCorrect
              ? `${
                  hardcodedTranslations.quiz.answer.correct[attemptLanguage]
                } ${stripHtml(selectedAnswer ?? '')}`
              : `${
                  hardcodedTranslations.quiz.answer.incorrect[attemptLanguage]
                } ${stripHtml(selectedAnswer ?? '')}`}
          </h5>
        </FlexContainer>
        {explanation && <RichTextRenderer content={explanation} rtl={rtl} />}
      </div>
      <AppButton
        severity="basic"
        label={hardcodedTranslations.buttons.next[attemptLanguage]}
        icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
        iconPos="right"
        rtl={rtl}
        onClick={onHide}
        autoFocus
      />
    </StyledFeedbackDialog>
  );
};
